import { EnvHelper } from "./helpers";
import * as Sentry from "@sentry/react";
import { AgentTrackingFeature } from "./helpers/agentTracking";

const PII_QUERY_PARAMS = [
  "ticket",
  "personsNames",
  "ownersNames",
  "customersNames",
  "suppliersNames",
  "receiversNames",
  "uploadedByUsersNames",
];

// The fragment on these pages is a link token that opens a leaving customer's material.
const LINK_TOKEN_PATHS = ["/offboarding"];

function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url, window.location.origin);
    for (const param of PII_QUERY_PARAMS) {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, "FILTERED");
      }
    }
    if (urlObj.hash && LINK_TOKEN_PATHS.includes(urlObj.pathname.toLowerCase())) {
      urlObj.hash = "FILTERED";
    }
    return urlObj.toString();
  } catch {
    return url;
  }
}

function sanitizeQueryString(queryString: string): string {
  try {
    const params = new URLSearchParams(queryString);
    for (const param of PII_QUERY_PARAMS) {
      if (params.has(param)) {
        params.set(param, "FILTERED");
      }
    }
    return params.toString();
  } catch {
    return queryString;
  }
}

function sanitizeEventUrls(event: Sentry.ErrorEvent): void {
  if (event.request?.url) {
    event.request.url = sanitizeUrl(event.request.url);
  }
  if (event.request?.query_string && typeof event.request.query_string === "string") {
    event.request.query_string = sanitizeQueryString(event.request.query_string);
  }
  const referer = event.request?.headers?.Referer ?? event.request?.headers?.referer;
  if (referer) {
    const key = event.request!.headers!.Referer ? "Referer" : "referer";
    event.request!.headers![key] = sanitizeUrl(referer);
  }
}

export default function initializeSentry() {
  const version = EnvHelper.getVersion();
  const name = EnvHelper.getName();
  const environment = EnvHelper.getEnvironment();
  const branch = EnvHelper.getBranch();
  const branchInfo = environment === "test" && branch ? `branch: ${branch}` : "";
  console.log("Initializing Sentry with version:", version, "environment:", environment, branchInfo);

  Sentry.init({
    dsn: EnvHelper.getSentryDSN(),
    // Enable performance monitoring
    tracesSampleRate: environment === "production" ? 0.2 : 1.0,

    beforeSend(event) {
      // Don't send events in development
      if (environment === "development") {
        return null;
      }

      // Filter out common noise
      if (event.exception?.values?.[0]?.type === "ChunkLoadError") {
        return null;
      }

      // Filter out network errors that are not actionable
      if (event.exception?.values?.[0]?.value?.includes("fetch")) {
        return null;
      }

      // Filter out cancelled requests
      if (
        event.exception?.values?.[0]?.value?.includes("cancelled") ||
        event.exception?.values?.[0]?.value?.includes("aborted")
      ) {
        return null;
      }

      // GDPR: Strip PII from URLs before sending to Sentry
      sanitizeEventUrls(event);

      return event;
    },

    // Logs carry the support agent's usage events only, so nothing else spends the logs quota.
    enableLogs: true,
    beforeSendLog(log) {
      if (environment === "development") return null;
      if (log.attributes?.feature !== AgentTrackingFeature) return null;

      // GDPR: URLs carry the company slug; the events name the company by id on purpose instead.
      const attributes = Object.fromEntries(
        Object.entries(log.attributes).filter(([key]) => !key.toLowerCase().includes("url"))
      );
      return { ...log, attributes };
    },

    beforeBreadcrumb(breadcrumb) {
      // GDPR: Sanitize URLs in navigation and network breadcrumbs
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = sanitizeUrl(breadcrumb.data.url);
      }
      if (breadcrumb.data?.from) {
        breadcrumb.data.from = sanitizeUrl(breadcrumb.data.from);
      }
      if (breadcrumb.data?.to) {
        breadcrumb.data.to = sanitizeUrl(breadcrumb.data.to);
      }
      return breadcrumb;
    },

    // Set release and environment
    release: `${name}@${version}`,
    environment: environment,
    sendDefaultPii: false,
    // Set initial scope
    initialScope: {
      tags: {
        app: name,
      },
    },
    ignoreErrors: ["UnhandledRejection", /^Request failed with status code 401$/],
  });
}
